import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModalInfo } from '../../../../shared/models';
import { CampaignService } from '../../../../shared/services/requests/campaign.service';
import { BehaviorSubject, catchError, of, Subject, switchMap, take, tap } from 'rxjs';
import {
  LinkedinCredentialMessagesResponse,
  Message,
  MessageRecipientInfo,
  MessageSenderInfo,
  MessageState,
  ProfileMessagesModalData,
} from '../../models/profile-messages.model';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { ProfileMessagesService } from '../../services';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-profile-messages-modal',
  templateUrl: './profile-messages-modal.component.html',
  styleUrls: ['./profile-messages-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProfileMessagesModalComponent implements OnInit, OnDestroy {
  private readonly senderDebounceTime = 300;

  private destroy$ = new Subject<void>();
  private senderSelect$ = new Subject<MessageSenderInfo | null>();

  senders: MessageSenderInfo[] = [];
  recipient: MessageRecipientInfo | null = null;

  selectedSenderId: string | null = null;
  messageState$ = new BehaviorSubject<MessageState>('loading');
  messages$ = new BehaviorSubject<Message[]>([]);
  isProfileConnecting$ = new Subject<boolean>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProfileMessagesModalData,
    public dialogRef: MatDialogRef<ProfileMessagesModalComponent>,
    private campaignService: CampaignService,
    private profileMessagesService: ProfileMessagesService,
    private translateService: TranslateService,
  ) {}

  info: ModalInfo = {
    type: 'large-modal',
    closeModalBtnId: 'close-btn-m',
    header: {
      title: 'MESSAGES.MODAL.TITLE',
      subtitle: 'MESSAGES.MODAL.SUBTITLE',
    },
    notScrollable: true,
  };

  ngOnInit(): void {
    this.initializeModal();
    this.setupSenderSelection();
  }

  private initializeModal(): void {
    const { recipient, senders } = this.data;

    this.recipient = recipient;
    this.senders = senders;

    if (!this.selectedSenderId) {
      const savedSenderId = this.profileMessagesService.getSavedSenderId();
      let senderToSelect = savedSenderId
        ? senders.find((s) => s.id === savedSenderId) || senders[0]
        : senders[0];

      if (!senderToSelect.isAccessible) {
        senderToSelect = senders.find((s) => s.isAccessible) || senderToSelect;
      }

      this.selectedSenderId = senderToSelect.id;
      this.senderSelect$.next(senderToSelect);
    }
  }

  private setupSenderSelection(): void {
    this.senderSelect$
      .pipe(
        debounceTime(this.senderDebounceTime),
        filter((sender) => sender !== null),
        tap(() => {
          this.messageState$.next('loading');
          this.messages$.next([]);
        }),
        switchMap((sender: MessageSenderInfo) =>
          this.campaignService.getLinkedinCredentialMessages(sender.id, this.recipient!.id).pipe(
            catchError(() => {
              this.messageState$.next('error');
              return of(null);
            }),
          ),
        ),
        filter(() => this.messageState$.value !== 'error'),
        tap((response: LinkedinCredentialMessagesResponse) => {
          if (!response) {
            this.messageState$.next('error');
            return;
          }

          const { messageList, sentConnection = false } = response;

          if (!messageList) {
            const status: MessageState = sentConnection ? 'sent-connection' : 'not-connected';
            this.messageState$.next(status);
            return;
          }

          if (messageList.length === 0) {
            this.messageState$.next('no-messages');
            this.messages$.next([]);
            return;
          }

          const clientMessages: Message[] = messageList.map((msg) => ({
            id: this.generateClientMessageId(),
            content: msg.message,
            timestamp: this.generateMessageTimestamp(msg.deliveredAt),
            isSent: msg.mine,
            date: new Date(msg.deliveredAt),
          }));

          const messagesWithSeparators = this.addDaySeparators(clientMessages);
          this.messages$.next(messagesWithSeparators);
          this.messageState$.next('loaded');
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeModal(event: Event): void {
    this.dialogRef.close(event);
  }

  onSenderSelect(sender: MessageSenderInfo): void {
    this.selectedSenderId = sender.id;
    this.profileMessagesService.saveSenderId(sender.id);
    this.senderSelect$.next(sender);
  }

  onSendMessage(message: string): void {
    this.campaignService
      .sendLinkedinMessage(this.selectedSenderId!, this.recipient!.id, message)
      .pipe(take(1))
      .subscribe(() => {
        const messages: Message[] = this.messages$.getValue();
        const updatedMessages = [
          ...messages,
          {
            id: this.generateClientMessageId(),
            content: message,
            timestamp: this.generateMessageTimestamp(),
            isSent: true,
            date: new Date(),
          },
        ];

        this.messages$.next(updatedMessages);
      });
  }

  onConnectProfile(): void {
    this.isProfileConnecting$.next(true);
    this.messageState$.next('loading');

    this.campaignService
      .connectWithProfile(this.selectedSenderId!, this.recipient!.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isProfileConnecting$.next(false);
          this.messageState$.next('sent-connection');
        },
        error: () => {
          this.isProfileConnecting$.next(false);
          this.messageState$.next('error');
        },
      });
  }

  private addDaySeparators(messages: Message[]): Message[] {
    const result: Message[] = [];
    let lastDate: string | null = null;

    messages.forEach((message) => {
      const messageDate = message.date ? new Date(message.date).toLocaleDateString() : '';

      if (messageDate !== lastDate) {
        lastDate = messageDate;

        result.push({
          id: 'separator-' + messageDate,
          content: this.formatDateLabel(message.date),
          timestamp: '',
          isSent: false,
          isDaySeparator: true,
        } as Message);
      }

      result.push(message);
    });

    return result;
  }

  private formatDateLabel(date: Date | undefined): string {
    if (!date) return '';

    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const isToday = messageDate.toLocaleDateString() === today.toLocaleDateString();
    const isYesterday = messageDate.toLocaleDateString() === yesterday.toLocaleDateString();

    if (isToday) {
      return this.translateService.instant('DATE.TODAY');
    } else if (isYesterday) {
      return this.translateService.instant('DATE.YESTERDAY');
    } else {
      return messageDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  }

  private generateClientMessageId(): string {
    return Date.now().toString() + Math.random().toString(36).substring(2);
  }

  private generateMessageTimestamp(deliveredAt: number | null = null): string {
    const date: Date = deliveredAt ? new Date(deliveredAt) : new Date();

    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
